








import { Vue, Component, Watch } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import { DeniedUsers, MediaComponentProps } from '@/store/videoCallRoom/types'
import { WebSocketClientInterface } from '@/websocket/types'

const DENIED_DELAY = 4000
const SWITCHING_DELAY = 200

enum Status {
  PENDING,
  SHOWING,
  REMOVING
}

type User = {
  id: number
  lastName: string
  firstName: string
}

type Queue = {
  id: number
  message: string
  status: Status
}

@Component
export default class VideoCallNotifyMessage extends Vue {
  $store!: ExStore
  messageQueue: Queue[] = []
  timer: number | null = null
  webSocket!: WebSocketClientInterface

  @Watch('deniedUsers')
  onChangeDeniedUsers(users: DeniedUsers, prevUsers: DeniedUsers) {
    if (!this.isCalling) return
    const newUserIds = Object.keys(users)
    const prevUserIds = Object.keys(prevUsers)
    const addedUserIds = newUserIds.filter(id => !prevUserIds.includes(id))
    this.messageQueue.push(
      ...addedUserIds.map(id => {
        const user = users[parseInt(id)]
        return {
          id: parseInt(id),
          message: this.getDeniedMessage(user),
          status: Status.PENDING
        }
      })
    )
  }

  @Watch('participants')
  onChangeParitcipants(
    paticipants: Array<MediaComponentProps>,
    prevParticipants: Array<MediaComponentProps>
  ) {
    if (!this.isCalling) return
    if (prevParticipants.length < paticipants.length) return
    const ids = paticipants.map(user => user.id)
    const leavedUsers = prevParticipants.filter(user => !ids.includes(user.id))
    this.messageQueue.push(
      ...leavedUsers.map(user => {
        return {
          id: user.id,
          message: this.getLeavedMessage(user),
          status: Status.PENDING
        }
      })
    )
  }

  @Watch('cancelledByCaller')
  onChangeCancelledByCaller(cancelled: boolean) {
    if (
      cancelled &&
      this.caller &&
      !this.messageQueue.find(q => q.id === this.caller?.id)
    ) {
      this.messageQueue.push({
        id: this.caller.id,
        message: this.getLeavedMessage(this.caller),
        status: Status.PENDING
      })
    }
  }

  get caller() {
    return this.$store.getters['videoCallRoom/caller']
  }

  get isCalling() {
    return this.$store.getters['videoCallRoom/isCalling']
  }

  get pendingUsers() {
    return this.$store.getters['videoCallRoom/pendingUsers']
  }

  get pendingUserArray() {
    return Object.values<User>(this.pendingUsers)
  }

  get deniedUsers() {
    return this.$store.getters['videoCallRoom/deniedUsers']
  }

  get deniedUserArray() {
    return Object.values<User>(this.deniedUsers)
  }

  get participants() {
    return this.$store.getters['videoCallRoom/participants']
  }

  get cancelledByCaller() {
    return this.$store.getters['videoCallRoom/cancelledByCaller']
  }

  get message() {
    if (!this.isCalling) return ''
    if (this.messageQueue.length) {
      return this.queuedMessage
    }
    if (this.participants.length < 2 && this.pendingUserArray.length) {
      return this.waitingMessage
    }
    return ''
  }

  get queuedMessage() {
    if (!this.messageQueue.length) return ''
    const queue = this.messageQueue[0]
    switch (queue.status) {
      case Status.PENDING:
        this.setTimerToMessage()
        return queue.message
      case Status.REMOVING:
        return ''
      default:
        return queue.message
    }
  }

  get waitingMessage() {
    const users = this.pendingUserArray.sort((user1, user2) => {
      const name1 = user1.lastName + user1.firstName
      const name2 = user2.lastName + user2.firstName
      if (name1 < name2) return -1
      if (name1 > name2) return 1
      return 0
    })
    const firstUser = users[0]
    const firstUserName = `${firstUser.lastName}${firstUser.firstName}`
    const message =
      users.length > 1
        ? `${firstUserName}さん他${users.length - 1}名の応答を待っています`
        : `${firstUserName}さんの応答を待っています`
    return message
  }

  created() {
    // alert('created')
    this.$store.commit('videoCallRoom/setCancelledByCaller', false)
    this.checkInitialDeniedUsers()
    this.checkAlreadyCallerCancelled()
  }

  checkAlreadyCallerCancelled() {
    if (this.cancelledByCaller && this.caller) {
      this.messageQueue.push({
        id: this.caller.id,
        message: this.getLeavedMessage(this.caller),
        status: Status.PENDING
      })
    }
  }

  checkInitialDeniedUsers() {
    if (this.deniedUserArray.length && !this.messageQueue.length) {
      this.deniedUserArray.forEach(user => {
        this.messageQueue.push({
          id: user.id,
          message: this.getDeniedMessage(user),
          status: Status.PENDING
        })
      })
    }
  }

  beforeDestroy() {
    if (this.timer) clearTimeout(this.timer)
  }

  setTimerToMessage() {
    const queue = this.messageQueue[0]
    queue.status = Status.SHOWING
    this.messageQueue.splice(0, 1, queue)
    this.timer = setTimeout(() => {
      queue.status = Status.REMOVING
      this.messageQueue.splice(0, 1, queue)
      this.timer = setTimeout(() => {
        this.messageQueue.splice(0, 1)
      }, SWITCHING_DELAY)
    }, DENIED_DELAY)
  }

  getDeniedMessage(user: User) {
    return `${user.lastName}${user.firstName}さんは応答できません`
  }

  getLeavedMessage(user: User) {
    return `${user.lastName}${user.firstName}さんが通話を終了しました`
  }
}
